// path: src/pages/IPA/Negociacoes/pages/DetalhesDaNegociacao/components/ResumoDeEnvioModal/ResumoDeEnvioModal.hooks.ts
import { useCallback, useEffect, useState } from 'react';

import { CostNegotiationMode } from '../../../NegociacaoFornecedor/costNegotiation';

export interface ColunaDoResumo {
  nome: string;
  /** Texto de tela: o que viaja ao motor é o `nome`, e o arquivo tem rótulo próprio. */
  rotulo: string;
  /** Só sai no cliente de custo de ENTRADA: no de líquido não há alíquota de entrada a afirmar. */
  sufixoDoCustoDeEntrada?: string;
  frase?: string;
  fixa: boolean;
  ligadaPorPadrao: boolean;
}

/**
 * O conjunto fechado, e o único lugar da tela que o conhece. A ordem é a da declaração no motor,
 * que é a ordem do arquivo; `fixa` diz que nenhuma seleção a desliga, e não onde ela aparece.
 */
export const COLUNAS_DO_RESUMO: ColunaDoResumo[] = [
  { nome: 'GRAO', rotulo: 'Cluster / loja / rede (grão da negociação)', fixa: true, ligadaPorPadrao: true },
  { nome: 'CODIGO_DO_PRODUTO', rotulo: 'Código do produto', fixa: true, ligadaPorPadrao: true },
  { nome: 'GTIN', rotulo: 'EAN', fixa: true, ligadaPorPadrao: true },
  { nome: 'DESCRICAO', rotulo: 'Descrição do produto', fixa: true, ligadaPorPadrao: true },
  {
    nome: 'CUSTO_ATUAL',
    rotulo: 'Custo atual',
    sufixoDoCustoDeEntrada: '(entrada)',
    frase: 'O ponto de partida da negociação.',
    fixa: false,
    ligadaPorPadrao: true,
  },
  {
    nome: 'NOVO_CUSTO_NEGOCIADO',
    rotulo: 'Novo custo negociado',
    sufixoDoCustoDeEntrada: '(entrada)',
    frase: 'O de/para que dá contexto ao pedido.',
    fixa: false,
    ligadaPorPadrao: true,
  },
  { nome: 'VERBA_A_CONCEDER', rotulo: 'Verba a conceder', sufixoDoCustoDeEntrada: '(custo de entrada)', fixa: true, ligadaPorPadrao: true },
  { nome: 'DATA_FIM', rotulo: 'Data fim da negociação', frase: 'Até quando o acordo vale.', fixa: false, ligadaPorPadrao: true },
  {
    nome: 'PRECO_DE_CONCORRENCIA',
    rotulo: 'Preço de concorrência',
    frase: 'A prova que sustenta o pleito — ligue quando quiser mostrá-la.',
    fixa: false,
    ligadaPorPadrao: false,
  },
];

/** O rótulo no modo de custo da negociação: no líquido, o sufixo de entrada não sai. */
export const rotuloDaColuna = (coluna: ColunaDoResumo, modo: CostNegotiationMode): string => {
  if (modo !== CostNegotiationMode.ENTRY_COST) return coluna.rotulo;
  if (!coluna.sufixoDoCustoDeEntrada) return coluna.rotulo;

  return `${coluna.rotulo} ${coluna.sufixoDoCustoDeEntrada}`;
};

export const COLUNAS_FIXAS = COLUNAS_DO_RESUMO.filter(coluna => coluna.fixa);
export const COLUNAS_OPCIONAIS = COLUNAS_DO_RESUMO.filter(coluna => !coluna.fixa);

export const OPCIONAIS_LIGADAS_POR_PADRAO = COLUNAS_OPCIONAIS.filter(coluna => coluna.ligadaPorPadrao).map(coluna => coluna.nome);

export const CHAVE_DA_SELECAO = 'ipa.negociacao.resumo.colunas';

const nomesConhecidos = new Set(COLUNAS_DO_RESUMO.map(coluna => coluna.nome));

/**
 * **O descarte do nome desconhecido mora aqui**, porque o motor recusa com 400: renomear uma
 * coluna pararia o Excel de quem já escolheu. **Ausência pede o padrão; lista vazia, só as fixas.**
 */
export function selecaoGuardada(bruto: string | null): string[] | undefined {
  if (bruto === null) return undefined;

  let lido: unknown;
  try {
    lido = JSON.parse(bruto);
  } catch {
    return undefined;
  }

  if (!Array.isArray(lido)) return undefined;
  if (lido.some(nome => typeof nome !== 'string')) return undefined;

  return (lido as string[]).filter(nome => nomesConhecidos.has(nome));
}

/** A leitura pode **lançar**, e não só devolver vazio: janela privada, dado de site bloqueado. */
export function lerSelecaoGuardada(): string[] | undefined {
  try {
    return selecaoGuardada(window.localStorage.getItem(CHAVE_DA_SELECAO));
  } catch {
    return undefined;
  }
}

export function guardarSelecao(nomes: string[]): void {
  try {
    window.localStorage.setItem(CHAVE_DA_SELECAO, JSON.stringify(nomes));
  } catch {
  }
}

export interface SelecaoDeColunas {
  marcadas: string[];
  /** `undefined` para quem nunca escolheu, e o motor responde com o padrão. */
  paraOPedido: string[] | undefined;
  definir: (nomes: string[]) => void;
}

/** Lembrada no navegador, e **guardada ao mudar a caixa**. `aberto` a relê: outra aba a muda. */
export function useSelecaoDeColunas(aberto: boolean | undefined): SelecaoDeColunas {
  const [escolhidas, setEscolhidas] = useState<string[] | undefined>(() => lerSelecaoGuardada());

  useEffect(() => {
    if (aberto) setEscolhidas(lerSelecaoGuardada());
  }, [aberto]);

  const marcadas = escolhidas ?? OPCIONAIS_LIGADAS_POR_PADRAO;

  const definir = useCallback((nomes: string[]) => {
    // A ordem é a da declaração, e nunca a dos cliques. Só opcionais entram: as fixas sempre vêm.
    const normalizado = COLUNAS_OPCIONAIS.filter(coluna => nomes.includes(coluna.nome)).map(coluna => coluna.nome);

    guardarSelecao(normalizado);
    setEscolhidas(normalizado);
  }, []);

  return { marcadas, paraOPedido: escolhidas, definir };
}
